import { get, post, put } from '../utils/request';
import { credentials, setUserProductGroups } from './user';
import { apiErrorMessage } from '../utils/apiErrorMessage';

export const handleFetchCatalogItems = (user) => async (dispatch) => {
  try {
    const response = await get('/catalog_items', dispatch, credentials(user));
    const body = await response.json();

    const { catalogItems } = body;

    dispatch(setCatalogItems(catalogItems));

    return true;
  } catch (error) {
    return false;
  }
};

// « Ajouter à mon catalogue » depuis un item de soumission déjà enregistré : le
// serveur copie la fiche réutilisable (prix, unité, dimension, catégorie de coût…) et
// dédoublonne par titre. Renvoie { outcome: 'created'|'updated', title } pour le toast.
export const handleAddItemToCatalog = (submissionItemId, user) => async (dispatch) => {
  try {
    const response = await post('/catalog_items/from_submission_item', { submission_item_id: submissionItemId }, dispatch, credentials(user));
    const body = await response.json();
    if (body && body.catalogItems) dispatch(setCatalogItems(body.catalogItems));
    return body || null;
  } catch (error) {
    return null;
  }
};

export const handleCreateNewCatalogItem = (params, user) => async (dispatch) => {
  try {
    const response = await post('/catalog_items', params, dispatch, credentials(user));
    const body = await response.json();

    const { catalogItems } = body;

    dispatch(setCatalogItems(catalogItems));

    return true;
  } catch (error) {
    return false;
  }
};

// Contrôle du fichier AVANT l'import : le serveur n'écrit rien et renvoie le rapport
// ligne à ligne que l'écran affiche.
export const handlePreviewCatalogItemsImport = (params, user) => async (dispatch) => {
  try {
    const response = await post('/import_catalog_items/preview', params, dispatch, credentials(user));
    const body = await response.json();

    if (!response.ok) return { ok: false, error: apiErrorMessage(body, response.status) };

    return { ok: true, report: body.report, blocking: body.blocking };
  } catch (error) {
    return { ok: false, error: apiErrorMessage(false) };
  }
};

// Import TOUT OU RIEN.
//
// Cette action annonçait un succès QUOI QU'IL ARRIVE : `fetch` ne lève pas d'exception
// sur un 422, donc le `catch` ne servait à rien et le `return { success: true }` était
// inconditionnel. Un fichier refusé par le serveur s'affichait « importé avec succès »,
// et le `dispatch` posait `undefined` dans le catalogue au passage.
export const handleImportCatalogItemsFromExcel = (params, user) => async (dispatch) => {
  try {
    const response = await post('/import_catalog_items', params, dispatch, credentials(user));
    const body = await response.json();

    if (!response.ok) {
      return { success: false, report: body.report, error: apiErrorMessage(body, response.status) };
    }

    if (body.catalogItems) dispatch(setCatalogItems(body.catalogItems));
    // La colonne « regroupement » en crée : sans ce rafraîchissement, le catalogue
    // groupait les fiches importées sous des regroupements qu'il ne connaissait pas
    // encore et n'en affichait aucune.
    if (body.productGroups) dispatch(setUserProductGroups(body.productGroups));

    return { success: true, imported: body.imported, report: body.report };
  } catch (error) {
    return { success: false, error: apiErrorMessage(false) };
  }
};

export const handleDeleteCatalogItems = (idsToDelete, user) => async (dispatch) => {
  try {
    const params = {
      ids: idsToDelete,
    };

    const response = await post('/delete_catalog_items', params, dispatch, credentials(user));
    const body = await response.json();

    const { catalogItems } = body;

    dispatch(setCatalogItems(catalogItems));

    return true;
  } catch (error) {
    return false;
  }
};

export const handleUpdateCatalogItem = (params, productId, user) => async (dispatch) => {
  try {
    const response = await put(`/catalog_items/${productId}`, params, dispatch, credentials(user));
    const body = await response.json();

    // Conflit de modification concurrente : recharge la liste fraîche et signale
    // à l'éditeur qu'il doit réhydrater et refaire sa modification.
    if (response.status === 409) {
      dispatch(setCatalogItems(body.catalogItems));
      return { conflict: true, message: body.message, fresh: body.catalogItems };
    }

    if (!response.ok) {
      return false;
    }

    const { catalogItems } = body;

    dispatch(setCatalogItems(catalogItems));

    return true;
  } catch (error) {
    return false;
  }
};

// Catégorisation : assigne une catégorie de coût (ou null) à un lot de produits.
export const handleAssignCostCategory = (ids, costCategoryId, user) => async (dispatch) => {
  try {
    const response = await post('/assign_cost_category', { ids, cost_category_id: costCategoryId }, dispatch, credentials(user));
    const body = await response.json();

    const { catalogItems } = body;

    dispatch(setCatalogItems(catalogItems));

    return true;
  } catch (error) {
    return false;
  }
};

// Regroupement : assigne un regroupement (ou null) à un lot de produits.
export const handleAssignProductGroup = (ids, productGroupId, user) => async (dispatch) => {
  try {
    const response = await post('/assign_product_group', { ids, product_group_id: productGroupId }, dispatch, credentials(user));
    const body = await response.json();

    const { catalogItems } = body;

    dispatch(setCatalogItems(catalogItems));

    return true;
  } catch (error) {
    return false;
  }
};

// « Revenir au prix fournisseur » — annule la personnalisation d'un produit lié
// et ré-active la synchro automatique (point 7).
export const handleRevertToStorePrice = (productId, user) => async (dispatch) => {
  try {
    const response = await post(`/catalog_items/${productId}/revert_to_store_price`, {}, dispatch, credentials(user));
    const body = await response.json();
    const { catalogItems } = body;
    dispatch(setCatalogItems(catalogItems));
    return true;
  } catch (error) {
    return false;
  }
};

// « Détacher » du catalogue Soumigo — garde le coût actuel, arrête la synchro (point 7).
export const handleDetachFromStore = (productId, user) => async (dispatch) => {
  try {
    const response = await post(`/catalog_items/${productId}/detach_from_store`, {}, dispatch, credentials(user));
    const body = await response.json();
    const { catalogItems } = body;
    dispatch(setCatalogItems(catalogItems));
    return true;
  } catch (error) {
    return false;
  }
};

export const setCatalogItems = (catalogItems) => ({
  type: 'SET_CATALOG_ITEMS',
  catalogItems,
});
